import React, { useState, useRef, useEffect } from 'react';
import { 
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  MessageSquare,
  Clock,
  Shield,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  AlertTriangle,
  Camera,
  Volume2
} from 'lucide-react';
import { useLegalVideoConsultation } from '../../hooks/useLegalVideoConsultation';

interface ClientConsultationProps {
  roomId?: string;
  clientName?: string;
  caseType?: string;
  attorneyName?: string;
  firmName?: string;
  consultationId?: string;
  requestId?: string;
  onLeaveConsultation?: () => void;
}

interface ChatMessage {
  id: string;
  sender: 'attorney' | 'client' | 'system';
  message: string;
  timestamp: Date;
  type: 'text' | 'file' | 'system';
}

export const ClientConsultation: React.FC<ClientConsultationProps> = ({
  roomId: initialRoomId,
  clientName = 'Client',
  requestId
}) => {
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const roomId = initialRoomId || urlParams.get('roomId') || '';
  const clientNameFromUrl = urlParams.get('clientName') || clientName;
  const requestIdFromUrl = urlParams.get('requestId') || requestId || '';

  // Use the legal video consultation hook
  const {
    room,
    participant,
    status,
    isConnected,
    isLoading,
    error,
    videoState,
    consultationTime,
    recordingConsent,
    formattedTime,
    joinAsClient,
    toggleVideo,
    toggleAudio,
    updateRecordingConsent,
    endConsultation
  } = useLegalVideoConsultation({
    onConsultationEnd: () => {
      console.log('Consultation ended');
      setShowEndedMessage(true);
    },
    onError: (error) => {
      console.error('Video consultation error:', error);
    }
  });

  // UI states
  const [showChat, setShowChat] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(true);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [devicesChecked, setDevicesChecked] = useState(false);
  const [showEndedMessage, setShowEndedMessage] = useState(false);

  // Consultation data
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'system',
      message: 'Welcome to your legal consultation. Your attorney will join shortly.',
      timestamp: new Date(),
      type: 'system'
    }
  ]);
  const [newMessage, setNewMessage] = useState('');

  // Refs
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Initialize as client when permissions are granted
  useEffect(() => {
    const initializeAsClient = async () => {
      if (roomId && permissionsGranted && devicesChecked) {
        try {
          console.log('[ClientConsultation] Joining as client:', roomId);
          await joinAsClient(roomId, clientNameFromUrl);
          setShowPermissionsModal(false);
        } catch (error) {
          console.error('Failed to join as client:', error);
        }
      }
    };

    initializeAsClient();
  }, [permissionsGranted, devicesChecked]); // Depend on permissions being granted

  // Check device permissions
  const checkDevicePermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      // Test devices work
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      setPermissionsGranted(true);
      setDevicesChecked(true);
      
      // Stop test stream
      setTimeout(() => {
        stream.getTracks().forEach(track => track.stop());
      }, 3000);
      
    } catch (error) {
      console.error('Failed to get device permissions:', error);
      setPermissionsGranted(false);
      setDevicesChecked(true);
    }
  };

  // Initialize local media stream
  useEffect(() => {
    const initializeMedia = async () => {
      try {
        if ((videoState.localVideo || videoState.localAudio) && isConnected) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: videoState.localVideo,
            audio: videoState.localAudio
          });
          
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
          
          console.log('[ClientConsultation] Local media initialized');
        }
      } catch (error) {
        console.error('Failed to initialize local media:', error);
      }
    };

    initializeMedia();
    
    return () => {
      if (localVideoRef.current?.srcObject) {
        const stream = localVideoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [videoState.localVideo, videoState.localAudio, isConnected]);

  const sendMessage = () => {
    if (!newMessage.trim()) return;

    const message: ChatMessage = {
      id: Date.now().toString(),
      sender: 'client',
      message: newMessage,
      timestamp: new Date(),
      type: 'text'
    };

    setChatMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  const handleEndConsultation = async () => {
    if (window.confirm('Are you sure you want to leave the consultation?')) {
      try {
        await endConsultation();
        setShowEndedMessage(true);
      } catch (error) {
        console.error('Failed to end consultation:', error);
      }
    }
  };

  // Show ended message
  if (showEndedMessage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Consultation Ended</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your consultation. Your attorney will follow up with you soon.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  // Show permissions modal
  if (showPermissionsModal && !isConnected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-lg w-full">
          <div className="text-center mb-6">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Legal Video Consultation</h2>
            <p className="text-gray-600">Room ID: {roomId}</p>
          </div>

          {!devicesChecked && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Before we begin:</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Camera className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Camera Access</p>
                    <p className="text-xs text-gray-600">We need access to your camera for video</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Volume2 className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Microphone Access</p>
                    <p className="text-xs text-gray-600">We need access to your microphone for audio</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {devicesChecked && !permissionsGranted && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <XCircle className="w-5 h-5 text-red-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-red-900">Permissions Required</p>
                  <p className="text-xs text-red-700 mt-1">
                    Please allow camera and microphone access to join the consultation.
                  </p>
                </div>
              </div>
            </div>
          )}

          {devicesChecked && permissionsGranted && (
            <>
              <div className="mb-6">
                <div className="bg-gray-900 rounded-lg overflow-hidden h-48 relative">
                  <video
                    ref={localVideoRef}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    playsInline
                  />
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    Device Test
                  </div>
                </div>
              </div>
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Devices Ready</p>
                    <p className="text-xs text-green-700 mt-1">
                      Your camera and microphone are working properly.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {!devicesChecked ? (
            <button
              onClick={checkDevicePermissions}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Check Devices
            </button>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="animate-spin h-6 w-6 text-blue-600" />
              <span className="ml-2 text-gray-600">Connecting to consultation...</span>
            </div>
          ) : (
            <button
              disabled={!permissionsGranted}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Joining Consultation...
            </button>
          )}
        </div>
      </div>
    );
  }

  // Main consultation view
  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      {/* Header Bar */}
      <div className="bg-gray-800 text-white px-6 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="font-semibold">Legal Consultation</h2>
            <p className="text-sm text-gray-300">Client: {clientNameFromUrl}</p>
          </div>
          <div className="bg-green-900 text-green-200 px-3 py-1 rounded-full text-xs flex items-center">
            <Shield size={12} className="mr-1" />
            Secure & Confidential
          </div>
        </div>

        <div className="flex items-center space-x-6">
          {/* Timer */}
          <div className="flex items-center text-lg font-mono">
            <Clock size={16} className="mr-2" />
            {formattedTime}
          </div>

          {/* Recording Indicator */}
          {videoState.recording && (
            <div className="bg-red-900 text-red-200 px-3 py-1 rounded-full text-xs flex items-center">
              <AlertTriangle size={12} className="mr-2" />
              Recording in Progress
            </div>
          )}

          {/* Connection Status */}
          {isLoading && (
            <div className="bg-yellow-900 text-yellow-200 px-3 py-1 rounded-full text-xs flex items-center">
              <Loader2 size={12} className="mr-2 animate-spin" />
              Connecting...
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Video Area */}
        <div className="flex-1 relative bg-black">
          {/* Remote Video (Attorney) */}
          <video
            ref={remoteVideoRef}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
          />
          
          {/* Local Video (Client) - Picture in Picture */}
          <div className="absolute bottom-4 right-4 w-32 h-24 bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-600">
            <video
              ref={localVideoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
            />
            {!videoState.localVideo && (
              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                <VideoOff className="text-gray-400" size={16} />
              </div>
            )}
          </div>

          {/* Waiting for Attorney Overlay */}
          {!isConnected && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="animate-spin h-12 w-12 text-white mx-auto mb-4" />
                <p className="text-white text-lg">Waiting for your attorney to join...</p>
              </div>
            </div>
          )}

          {/* Controls Overlay - Limited for Client */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <div className="bg-gray-800 bg-opacity-90 rounded-full px-4 py-2 flex items-center space-x-3">
              {/* Audio */}
              <button
                onClick={toggleAudio}
                className={`p-2 rounded-full transition-colors ${
                  videoState.localAudio 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {videoState.localAudio ? <Mic size={18} /> : <MicOff size={18} />}
              </button>

              {/* Video */}
              <button
                onClick={toggleVideo}
                className={`p-2 rounded-full transition-colors ${
                  videoState.localVideo 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {videoState.localVideo ? <Video size={18} /> : <VideoOff size={18} />}
              </button>

              {/* Chat */}
              <button
                onClick={() => setShowChat(!showChat)}
                className={`p-2 rounded-full transition-colors ${
                  showChat 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                <MessageSquare size={18} />
              </button>

              {/* End Call */}
              <button
                onClick={handleEndConsultation}
                className="p-2 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
              >
                <Phone size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div className="w-80 bg-white flex flex-col">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <h3 className="font-semibold text-gray-900">Chat</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`${
                    msg.sender === 'system' 
                      ? 'text-center' 
                      : msg.sender === 'client' 
                        ? 'ml-auto' 
                        : 'mr-auto'
                  }`}
                >
                  {msg.sender === 'system' ? (
                    <div className="bg-blue-50 text-blue-800 text-xs px-3 py-2 rounded-lg">
                      {msg.message}
                    </div>
                  ) : (
                    <div
                      className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                        msg.sender === 'client'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-900'
                      }`}
                    >
                      <p>{msg.message}</p>
                      <p className="text-xs opacity-75 mt-1">
                        {msg.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Type a message..."
                />
                <button
                  onClick={sendMessage}
                  className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recording Consent Modal */}
      {!recordingConsent && videoState.recording && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-amber-500 mr-3" size={24} />
              <h3 className="text-lg font-semibold">Recording Consent Required</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Your attorney would like to record this consultation for your case file. 
              The recording will be kept confidential under attorney-client privilege.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => updateRecordingConsent(true)}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                I Consent
              </button>
              <button
                onClick={() => updateRecordingConsent(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
              >
                No Recording
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};